import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';

// Performance thresholds (in milliseconds)
const THRESHOLDS = {
  LCP: { good: 2500, needsImprovement: 4000 },
  CLS: { good: 0.1, needsImprovement: 0.25 },
  FCP: { good: 1800, needsImprovement: 3000 },
  TTFB: { good: 800, needsImprovement: 1800 },
  INP: { good: 200, needsImprovement: 500 },
};

// Rating helper
function getRating(value: number, metric: keyof typeof THRESHOLDS): 'good' | 'needs-improvement' | 'poor' {
  const threshold = THRESHOLDS[metric];
  if (metric === 'CLS') {
    if (value <= threshold.good) return 'good';
    if (value <= threshold.needsImprovement) return 'needs-improvement';
    return 'poor';
  }
  if (value <= threshold.good) return 'good';
  if (value <= threshold.needsImprovement) return 'needs-improvement';
  return 'poor';
}

// Send to analytics (GTM, GA4, or custom endpoint)
function sendToAnalytics(metric: any) {
  const { name, value, rating, id } = metric;

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`📊 [Web Vitals] ${name}:`, {
      value: Math.round(name === 'CLS' ? value * 1000 : value),
      rating,
      id,
    });
  }

  // Send to Google Analytics 4 (if available)
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', name, {
      event_category: 'Web Vitals',
      event_label: id,
      value: Math.round(name === 'CLS' ? value * 1000 : value),
      non_interaction: true,
      metric_rating: rating,
    });
  }

  // Send to GTM dataLayer (if available)
  if (typeof window !== 'undefined' && (window as any).dataLayer) {
    (window as any).dataLayer.push({
      event: 'web_vitals',
      metric_name: name,
      metric_value: Math.round(name === 'CLS' ? value * 1000 : value),
      metric_rating: rating,
      metric_id: id,
    });
  }

  // Optional: Send to custom endpoint
  // navigator.sendBeacon('/api/analytics', JSON.stringify(metric));
}

// Report Web Vitals
export function reportWebVitals() {
  if (typeof window === 'undefined') return;

  try {
    // Largest Contentful Paint (LCP) - measures loading performance
    onLCP((metric) => {
      const rating = getRating(metric.value, 'LCP');
      sendToAnalytics({ ...metric, rating });
    });

    // Cumulative Layout Shift (CLS) - measures visual stability
    onCLS((metric) => {
      const rating = getRating(metric.value, 'CLS');
      sendToAnalytics({ ...metric, rating });
    });

    // First Contentful Paint (FCP) - measures perceived load speed
    onFCP((metric) => {
      const rating = getRating(metric.value, 'FCP');
      sendToAnalytics({ ...metric, rating });
    });

    // First Contentful Paint (FCP) - measures perceived load speed
    onFCP((metric) => {
      const rating = getRating(metric.value, 'FCP');
      sendToAnalytics({ ...metric, rating });
    });

    // Time to First Byte (TTFB) - measures server response time
    onTTFB((metric) => {
      const rating = getRating(metric.value, 'TTFB');
      sendToAnalytics({ ...metric, rating });
    });

    // Interaction to Next Paint (INP) - measures responsiveness
    onINP((metric) => {
      const rating = getRating(metric.value, 'INP');
      sendToAnalytics({ ...metric, rating });
    });
  } catch (error) {
    console.error('Error reporting web vitals:', error);
  }
}

// Export for Next.js custom App
export default reportWebVitals;
